import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Drawer, Input, Space, Spin, message } from "antd";
import dayjs from "dayjs";
import {
  fetchSprZalGrafFact,
  setSprZalGrafFactIsOk,
  setSprZalGrafFactIsOkAll
} from "./spr-zal-api";
import "./spr-zal-graf-fact-modal.css";

const DRAWER_WIDTH_KEY = "planOnline.sprZal.grafFactDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 520;

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatHm = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  if (d.isValid()) return d.format("HH:mm");
  const m = String(value).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
};

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const normalizeRow = (row) => ({
  key: `${pick(row, "idZal", "IdZal")}-${pick(row, "idStr", "IdStr")}-${pick(row, "idClient", "IdClient")}-${pick(row, "idClientStrUsl", "IdClientStrUsl")}`,
  idZal: toNumber(pick(row, "idZal", "IdZal")),
  idStr: toNumber(pick(row, "idStr", "IdStr")),
  idClient: toNumber(pick(row, "idClient", "IdClient")),
  idClientStrUsl: toNumber(pick(row, "idClientStrUsl", "IdClientStrUsl")),
  isOk: (toNumber(pick(row, "isOk", "IsOk")) ?? 0) === 1,
  isDel: (toNumber(pick(row, "isDel", "IsDel")) ?? 0) === 1,
  timeBeg: formatHm(pick(row, "timeBeg", "TimeBeg")),
  timeEnd: formatHm(pick(row, "timeEnd", "TimeEnd")),
  clientName: pick(row, "clientName", "ClientName") || "",
  uslName: pick(row, "uslName", "UslName") || "",
  isPlat: (toNumber(pick(row, "isPlat", "IsPlat")) ?? 0) === 1,
  vrachName: pick(row, "vrachName", "VrachName") || "",
  naznName: pick(row, "naznName", "NaznName") || "",
  sotrName: pick(row, "sotrName", "SotrName") || ""
});

/**
 * Факт прохождения процедур (Delphi TFrmActionPlanFactZal).
 */
const SprZalGrafFactModal = ({ open, idZal, fdate, idStr, timeLabel, zalName, onClose, onChanged }) => {
  const [size, setSize] = useState(loadWidth);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [error, setError] = useState("");
  const [clientFilter, setClientFilter] = useState("");

  const dateLabel = fdate ? dayjs(fdate).format("DD.MM.YYYY") : "";

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const load = useCallback(() => {
    if (!open || idZal == null || !fdate) return;
    setLoading(true);
    setError("");
    fetchSprZalGrafFact(idZal, fdate, idStr || 0)
      .then((data) => {
        const list = Array.isArray(data) ? data.map(normalizeRow) : [];
        setRows(list);
        setCheckedKeys((prev) => prev.filter((key) => list.some((r) => r.key === key)));
        if (!list.length) setError("Запланированных визитов нет");
      })
      .catch((e) => {
        setRows([]);
        setError(e.message || "Не удалось загрузить факт");
      })
      .finally(() => setLoading(false));
  }, [open, idZal, fdate, idStr]);

  useEffect(() => {
    if (!open) return;
    setClientFilter("");
    setCheckedKeys([]);
    load();
  }, [open, load]);

  const filteredRows = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.clientName || "").toLowerCase().includes(q));
  }, [rows, clientFilter]);

  const checkedRows = useMemo(
    () => filteredRows.filter((r) => checkedKeys.includes(r.key)),
    [filteredRows, checkedKeys]
  );
  const canMark = checkedRows.some((r) => !r.isOk && !r.isDel);
  const canUnmark = checkedRows.some((r) => r.isOk);

  const toggleChecked = (key) => {
    setCheckedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const setMany = async (targets, isOk) => {
    const list = targets.filter((r) => (isOk ? !r.isOk && !r.isDel : r.isOk));
    if (!list.length) return;
    setBusy(true);
    try {
      for (const row of list) {
        await setSprZalGrafFactIsOk(idZal, {
          idStr: row.idStr,
          idClient: row.idClient,
          idClientStrUsl: row.idClientStrUsl,
          isOk: isOk ? 1 : 0
        });
      }
      await load();
      onChanged?.();
    } catch (e) {
      message.error(e.message || "Не удалось изменить факт");
    } finally {
      setBusy(false);
    }
  };

  const toggleOne = (row) => {
    if (busy || row.isDel) return;
    setMany([row], !row.isOk);
  };

  const setAll = async (isOk) => {
    setBusy(true);
    try {
      const res = await setSprZalGrafFactIsOkAll(idZal, fdate, isOk ? 1 : 0, idStr || 0);
      const skipped = toNumber(pick(res, "skipped", "Skipped")) ?? 0;
      if (isOk && skipped > 0) {
        message.warning(`Нельзя отметить пройденной ${skipped} отмененных записей`);
      }
      await load();
      onChanged?.();
    } catch (e) {
      message.error(e.message || "Не удалось изменить факт");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      placement="right"
      title="Факт прохождения процедур"
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={onClose}
      destroyOnClose
      rootClassName="spr-zal-graf-fact-drawer"
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
      footer={
        <div className="spr-zal-graf-fact__footer">
          <Button onClick={onClose}>Закрыть</Button>
        </div>
      }
    >
      <div className="spr-zal-graf-fact">
        <div className="spr-zal-graf-fact__info">
          Факт прохождения процедур в кабинете {zalName || ""} за {dateLabel}
          {idStr ? `, сеанс ${timeLabel || ""}` : ""}
        </div>
        <Space wrap className="spr-zal-graf-fact__actions">
          <Button disabled={!canMark || busy} onClick={() => setMany(checkedRows, true)}>
            Отметить
          </Button>
          <Button disabled={!canUnmark || busy} onClick={() => setMany(checkedRows, false)}>
            Снять
          </Button>
          <Button disabled={busy || !filteredRows.length} onClick={() => setAll(true)}>
            Отметить все
          </Button>
          <Button disabled={busy || !filteredRows.length} onClick={() => setAll(false)}>
            Снять все
          </Button>
        </Space>
        <Input
          allowClear
          className="spr-zal-graf-fact__filter"
          placeholder="Фильтр по имени клиента"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
        />
        {error && !rows.length ? (
          <Alert type="info" showIcon message={error} className="spr-zal-graf-fact__alert" />
        ) : null}
        {!error && rows.length && !filteredRows.length ? (
          <Alert
            type="info"
            showIcon
            message="Нет клиентов по фильтру"
            className="spr-zal-graf-fact__alert"
          />
        ) : null}
        <div className="spr-zal-graf-fact__list">
          <Spin spinning={loading || busy}>
            <div className="spr-zal-graf-fact__blocks">
              {filteredRows.map((row) => {
                const checked = checkedKeys.includes(row.key);
                const status = row.isDel ? "del" : row.isOk ? "ok" : "pending";
                return (
                  <div
                    key={row.key}
                    role="button"
                    tabIndex={0}
                    className={[
                      "spr-zal-graf-fact__block",
                      `spr-zal-graf-fact__block--${status}`,
                      checked ? "spr-zal-graf-fact__block--selected" : ""
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={row.isDel ? undefined : "Двойной клик — переключить факт"}
                    onClick={() => toggleChecked(row.key)}
                    onDoubleClick={() => toggleOne(row)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggleChecked(row.key);
                      }
                    }}
                  >
                    <div className="spr-zal-graf-fact__block-top">
                      <span className="spr-zal-graf-fact__time">
                        <Checkbox
                          checked={checked}
                          className="spr-zal-graf-fact__check"
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => e.stopPropagation()}
                          onChange={() => toggleChecked(row.key)}
                        />
                        {row.timeBeg}
                        {row.timeEnd ? ` – ${row.timeEnd}` : ""}
                      </span>
                      <span className={`spr-zal-graf-fact__status spr-zal-graf-fact__status--${status}`}>
                        {row.isDel ? "Отменён" : row.isOk ? "Прошёл" : "Ожидает"}
                      </span>
                    </div>
                    <div className="spr-zal-graf-fact__client">{row.clientName || "—"}</div>
                    <div className="spr-zal-graf-fact__usl">
                      {row.uslName || "—"}
                      {row.isPlat ? <span className="spr-zal-graf-fact__plat"> · платно</span> : null}
                    </div>
                    <div className="spr-zal-graf-fact__meta">
                      {row.vrachName ? <span>Врач: {row.vrachName}</span> : null}
                      {row.naznName ? <span>Назначил: {row.naznName}</span> : null}
                      {row.sotrName ? <span>Исполнитель: {row.sotrName}</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </Spin>
        </div>
      </div>
    </Drawer>
  );
};

export default SprZalGrafFactModal;
